import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { getDashboardApi } from '../../api/adminApi';

import DashTab       from './DashTab';
import ProductsTab   from './ProductsTab';
import OrdersTab     from './OrdersTab';
import CustomersTab  from './CustomersTab';
import CategoriesTab from './CategoriesTab';
import CouponsTab    from './CouponsTab';
import InventoryTab  from './InventoryTab';
import AnalyticsTab  from './AnalyticsTab';
import ReportsTab    from './ReportsTab';
import BannersTab    from './BannersTab';
import SettingsTab   from './SettingsTab';
import AdminUsersTab from './AdminUsersTab';

const NAV = [
  { group: 'Overview', items: [
    { key: 'dashboard',    icon: '▦',  label: 'Dashboard' },
    { key: 'analytics',   icon: '📊', label: 'Analytics' },
  ]},
  { group: 'Catalogue', items: [
    { key: 'products',    icon: '◫',  label: 'Products' },
    { key: 'categories',  icon: '◧',  label: 'Categories' },
    { key: 'inventory',   icon: '📦', label: 'Inventory',  badgeKey: 'lowStock' },
    { key: 'banners',     icon: '🖼',  label: 'Banners' },
  ]},
  { group: 'Commerce',  items: [
    { key: 'orders',      icon: '⊟',  label: 'Orders',    badgeKey: 'pendingOrders' },
    { key: 'customers',   icon: '◎',  label: 'Customers' },
    { key: 'coupons',     icon: '◈',  label: 'Coupons' },
  ]},
  { group: 'Data',      items: [
    { key: 'reports',     icon: '📋', label: 'Reports' },
  ]},
  { group: 'Admin',     items: [
    { key: 'settings',    icon: '⚙',  label: 'Settings' },
    { key: 'admin-users', icon: '👤', label: 'Admin Users' },
  ]},
];

const TAB_LABEL = {
  dashboard: 'Dashboard', analytics: 'Analytics', products: 'Products', categories: 'Categories',
  inventory: 'Inventory', banners: 'Banners', orders: 'Orders', customers: 'Customers',
  coupons: 'Coupons', reports: 'Reports', settings: 'Settings', 'admin-users': 'Admin Users',
};

export default function AdminDashboard() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user }  = useSelector((s) => s.auth);
  const [tab, setTab]             = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [badges, setBadges]       = useState({ pendingOrders: 0, lowStock: 0 });

  useEffect(() => {
    getDashboardApi().then((r) => {
      setBadges({
        pendingOrders: r.data.pending_orders || 0,
        lowStock: (r.data.low_stock || []).length || 0,
      });
    }).catch(() => {});
  }, []);

  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  const renderTab = () => {
    switch (tab) {
      case 'dashboard':   return <DashTab user={user} />;
      case 'analytics':   return <AnalyticsTab />;
      case 'products':    return <ProductsTab />;
      case 'categories':  return <CategoriesTab />;
      case 'inventory':   return <InventoryTab />;
      case 'banners':     return <BannersTab />;
      case 'orders':      return <OrdersTab />;
      case 'customers':   return <CustomersTab />;
      case 'coupons':     return <CouponsTab />;
      case 'reports':     return <ReportsTab />;
      case 'settings':    return <SettingsTab />;
      case 'admin-users': return <AdminUsersTab />;
      default:            return null;
    }
  };

  return (
    <div style={S.root}>
      {/* ── Top Bar ─────────────────────────────────────────── */}
      <header style={S.topBar}>
        <div style={S.topLeft}>
          <button style={S.collapseBtn} onClick={() => setCollapsed((c) => !c)} title="Toggle sidebar">
            <span style={S.hamburger} /><span style={S.hamburger} /><span style={S.hamburger} />
          </button>
          <Link to="/" style={S.logoLink}>
            <img src="/logo-transparent.png" alt="Bhadralok" style={{ height: '34px', width: 'auto' }} />
          </Link>
          <div style={S.divider} />
          <span style={S.adminLabel}>ADMIN</span>
          <span style={S.breadcrumb}>/ {TAB_LABEL[tab]}</span>
        </div>
        <div style={S.topRight}>
          <Link to="/" style={S.viewSiteBtn} target="_blank" rel="noopener noreferrer">↗ View Store</Link>
          <div style={S.userChip}>
            <div style={S.userAvatar}>{user?.name?.[0]?.toUpperCase()}</div>
            <div style={S.userInfo}>
              <p style={S.userName}>{user?.name}</p>
              <p style={S.userRole}>Administrator</p>
            </div>
            <button style={S.logoutBtn} onClick={handleLogout} title="Logout">⏻</button>
          </div>
        </div>
      </header>

      {/* ── Layout ──────────────────────────────────────────── */}
      <div style={S.body}>
        {/* Sidebar */}
        <aside style={{ ...S.sidebar, width: collapsed ? '0px' : '220px', overflow: collapsed ? 'hidden' : 'visible', minWidth: collapsed ? '0' : '220px' }}>
          <div style={S.sidebarInner}>
            {NAV.map((group) => (
              <div key={group.group} style={S.navGroup}>
                <p style={S.groupLabel}>{group.group}</p>
                {group.items.map((item) => {
                  const badgeNum = item.badgeKey ? badges[item.badgeKey] : 0;
                  return (
                    <button key={item.key}
                      style={{ ...S.navItem, ...(tab === item.key ? S.navItemActive : {}) }}
                      onClick={() => setTab(item.key)}>
                      <span style={{ ...S.navIcon, color: tab === item.key ? 'var(--red)' : 'rgba(255,255,255,.35)' }}>{item.icon}</span>
                      <span style={{ color: tab === item.key ? '#fff' : 'rgba(255,255,255,.6)', fontWeight: tab === item.key ? 700 : 400, flex: 1 }}>
                        {item.label}
                      </span>
                      {badgeNum > 0 && (
                        <span style={{ ...S.badge, background: item.badgeKey === 'lowStock' ? '#D97706' : 'var(--red)' }}>
                          {badgeNum}
                        </span>
                      )}
                      {tab === item.key && <div style={S.activeBar} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={S.sideFooter}>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,.2)', letterSpacing: '1px', margin: 0 }}>BHADRALOK v1.0</p>
          </div>
        </aside>

        {/* Main Content */}
        <main style={S.main}>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            .admin-tr:hover { background: #FAFAFA !important; }
            .admin-iconbtn:hover { background: #F3F4F6 !important; }
          `}</style>
          {renderTab()}
        </main>
      </div>
    </div>
  );
}

const S = {
  root:         { display: 'flex', flexDirection: 'column', height: '100vh', background: '#F5F5F5', fontFamily: 'var(--font-body)' },
  topBar:       { height: '56px', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', flexShrink: 0, borderBottom: '2px solid var(--red)', zIndex: 100, position: 'sticky', top: 0 },
  topLeft:      { display: 'flex', alignItems: 'center', gap: '14px' },
  collapseBtn:  { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px', padding: '6px', opacity: .7 },
  hamburger:    { display: 'block', width: '18px', height: '2px', background: '#fff', borderRadius: '1px' },
  logoLink:     { display: 'flex', alignItems: 'center', textDecoration: 'none' },
  divider:      { width: '1px', height: '20px', background: 'rgba(255,255,255,.15)' },
  adminLabel:   { fontFamily: 'var(--font-display)', fontSize: '13px', letterSpacing: '3px', color: 'rgba(255,255,255,.35)' },
  breadcrumb:   { fontSize: '12px', color: 'rgba(255,255,255,.4)', letterSpacing: '.5px' },
  topRight:     { display: 'flex', alignItems: 'center', gap: '12px' },
  viewSiteBtn:  { fontSize: '11px', fontWeight: 700, letterSpacing: '1px', color: 'rgba(255,255,255,.5)', textDecoration: 'none', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', padding: '5px 12px', transition: 'all .2s' },
  userChip:     { display: 'flex', alignItems: 'center', gap: '10px' },
  userAvatar:   { width: '30px', height: '30px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '13px', color: '#fff', flexShrink: 0 },
  userInfo:     { display: 'flex', flexDirection: 'column' },
  userName:     { fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,.8)', margin: 0, lineHeight: 1 },
  userRole:     { fontSize: '10px', color: 'rgba(255,255,255,.3)', margin: 0, letterSpacing: '.5px', marginTop: '2px' },
  logoutBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,.4)', fontSize: '16px', padding: '4px 8px', lineHeight: 1, transition: 'color .2s' },
  body:         { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebar:      { background: '#1A1A1A', display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'width .25s, min-width .25s', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,.05)' },
  sidebarInner: { flex: 1, padding: '16px 0 8px' },
  navGroup:     { marginBottom: '8px' },
  groupLabel:   { fontSize: '9px', fontWeight: 700, letterSpacing: '2.5px', color: 'rgba(255,255,255,.2)', textTransform: 'uppercase', padding: '8px 20px 4px', margin: 0 },
  navItem:      { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-body)', fontSize: '13px', letterSpacing: '.3px', position: 'relative', transition: 'background .15s', whiteSpace: 'nowrap', minWidth: '220px' },
  navItemActive:{ background: 'rgba(255,255,255,.06)' },
  navIcon:      { fontSize: '16px', lineHeight: 1, flexShrink: 0, width: '20px', textAlign: 'center' },
  activeBar:    { position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'var(--red)' },
  badge:        { fontSize: '10px', fontWeight: 700, color: '#fff', padding: '1px 6px', borderRadius: '99px', minWidth: '18px', textAlign: 'center', lineHeight: '16px' },
  sideFooter:   { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.05)' },
  main:         { flex: 1, overflowY: 'auto', padding: '28px 32px' },
};
